import { property } from 'lit/decorators.js';
import { BaseSection } from '@vitesse/base-ui/components/base-section';
import type { ModelManager } from '../../managers/model-manager';
import type { ModelView } from '../../../core/domain/model';

/*
 * ModelSectionBase — a BaseSection bound to one model.
 *
 * Adds the data layer every model section shares: a `modelId` + the
 * ModelManager, a `model` getter reading the manager cache, and a subscription
 * that re-renders on 'change'. Concrete model sections `extends ModelSectionBase`
 * and implement renderContent() (+ sectionHeading()/renderActions()).
 *
 * Path-only-ish prop shape (modelId + the manager) → the same element renders a
 * section on the detail page OR as a standalone dashboard widget.
 */
export abstract class ModelSectionBase extends BaseSection {
  @property() modelId = '';
  @property({ attribute: false }) models?: ModelManager;

  protected get model(): ModelView | undefined {
    return this.models?.get(this.modelId);
  }

  private readonly onModelsChange = (): void => {
    this.requestUpdate();
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.models?.addEventListener('change', this.onModelsChange);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.models?.removeEventListener('change', this.onModelsChange);
  }
}
